import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import InviteItem from './InviteItem';
import LoadingSpinner from '../../components/LoadingSpinner';

// IMAGES
import FallbackImage from '../../assets/svg/image-fallback-square.svg';

// TYPES
import { InviteInviteLinksInviteDetails, AccountUserUserProfile as OrganisationItemType } from '../../api/models';

const InviteToJoin: React.FC = () => {
	const user = useAppSelector((state) => state.user);

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Check if Organisation is active
	const isOrganisationActive = useAppSelector((state) => state.user.isOrganisationActive);

	// Set up Data state
	const [activeOrganisation, setActiveOrganisation] = React.useState<OrganisationItemType>({});

	const [inviteToJoinsData, setInviteToJoins] = React.useState<InviteInviteLinksInviteDetails>({});

	const { organisationIdentifier, availableRoles } = inviteToJoinsData;

	const getInviteToJoinsData = async () => {
		setIsLoading(true);

		const response = await apibridge.getInviteToJoinsData();

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = {} } = response;

			setIsLoading(false);

			setInviteToJoins(result);
		}
	};

	React.useEffect(() => {
		if (user.activeProfileId && user.profiles) {
			getInviteToJoinsData();

			setActiveOrganisation(user.profiles?.filter((profile) => profile.id === user.activeProfileId)[0]);
		}
	}, [user]);

	// Set up fall back image
	const [imageSrc, setImageSrc] = React.useState<string | undefined>(
		activeOrganisation ? activeOrganisation.image : FallbackImage
	);

	React.useEffect(() => {
		setImageSrc((activeOrganisation && activeOrganisation.image) || FallbackImage);
	}, [activeOrganisation && activeOrganisation.image]);

	return (
		<div className="page-general-layout invite-to-join-page wide-gap">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<img
									src={imageSrc}
									alt="organisation logo"
									className="general-square-image"
									onError={() => {
										if (imageSrc !== FallbackImage) {
											setImageSrc(FallbackImage);
										}
									}}
								/>

								<h2 className="header-text mtop-30">Invite staff, families or visitors to join</h2>

								<h2 className="header-text">{activeOrganisation?.name}</h2>

								<p className="header-info">
									Select the role the invitee should join as, copy the link and send via email or SMS.
								</p>

								<article className="invite-links-section">
									{isOrganisationActive &&
										availableRoles &&
										availableRoles.length > 0 &&
										availableRoles.map((role, index) => {
											return <InviteItem key={index} role={role} organisationIdentifier={organisationIdentifier} />;
										})}
								</article>
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default InviteToJoin;
